import { useState, useEffect } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { CardElement } from '@stripe/react-stripe-js';
import { toast } from 'react-hot-toast';
import { useCreateReturnShippingLabelMutation } from '../redux/apis/endpoints/deliveryApi';

export const useReturnLabelGeneration = ({
  returnDetails,
  fulfillmentGroupId,
  paymentAmount,
  onPaymentSuccess,
  onPaymentError,
  savedPaymentMethods,
  isReturn = true
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(savedPaymentMethods[0]?.id || '');
  const [createReturnShippingLabel, { isLoading: isGeneratingReturnLabelApi }] = useCreateReturnShippingLabelMutation();
  
  // State to store extracted parcel info
  const [parcels, setParcels] = useState(null);
  
  // Add state for label success
  const [labelData, setLabelData] = useState(null);
  const [showLabelSuccess, setShowLabelSuccess] = useState(false);
  
  // Extract parcel information as soon as returnDetails are available
  useEffect(() => {
    if (returnDetails?.orderItems?.length > 0) {
      try {
        const extractedParcels = returnDetails.orderItems.map(item => {
          const parcelInfo = item?.parcelInfo || item?.product?.parcelInfo;
          if (!parcelInfo) {
            console.error(`Missing parcel info for item ${item.id}`);
            throw new Error(`Missing parcel information for item ${item.id}`);
          }
          
          // Validate required fields
          if (!parcelInfo.weight || !parcelInfo.units?.mass || 
              !parcelInfo.length || !parcelInfo.width || !parcelInfo.height || !parcelInfo.units?.distance) {
            console.error(`Incomplete parcel info for item ${item.id}:`, parcelInfo);
            throw new Error(`Incomplete parcel information for item ${item.id}`);
          }
          
          return {
            weight: String(parcelInfo.weight),
            mass_unit: parcelInfo.units.mass || 'kg',
            length: String(parcelInfo.length),
            width: String(parcelInfo.width),
            height: String(parcelInfo.height),
            distance_unit: parcelInfo.units.distance || 'cm'
          };
        });
        
        console.log('[useReturnLabelGeneration] Extracted parcels information:', extractedParcels);
        setParcels(extractedParcels);
      } catch (error) {
        console.error('[useReturnLabelGeneration] Error extracting parcel information:', error);
        // We don't set error state here as we want to allow the user to continue and see the API error
      }
    }
  }, [returnDetails]);

  const handleGenerateLabel = async (selectedCustomerAddress, selectedMerchantAddress, selectedRate, requiresPayment) => {
    try {
      if (!selectedRate) {
        toast.error('Please select a shipping rate first');
        return;
      }
      
      // Use parcels if we have them, but don't require them
      console.log('[useReturnLabelGeneration] Using parcels for label generation:', parcels);
      
      if (requiresPayment) {
        if (!stripe || !elements) {
          throw new Error('Stripe has not initialized yet. Please try again.');
        }
        
        // Use saved payment method or collect new payment details
        let paymentMethod;
        if (selectedPaymentMethod) {
          // Use existing payment method
          paymentMethod = { payment_method: selectedPaymentMethod };
        } else {
          // Get card details from CardElement
          const cardElement = elements.getElement(CardElement);
          if (!cardElement) {
            throw new Error('Card element not found');
          }
          
          // Create payment method
          const { error, paymentMethod: newPaymentMethod } = await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
          });
          
          if (error) {
            throw new Error(error.message);
          }
          
          paymentMethod = { payment_method: newPaymentMethod.id };
        }
        
        // Create payment intent on server - only include parcels if available
        const response = await createReturnShippingLabel({
          fulfillmentGroupId,
          // Group-level returns don't need orderItemId
          fromAddressId: selectedCustomerAddress?.id,
          toAddressId: selectedMerchantAddress?.id && !selectedMerchantAddress.id.toString().startsWith('merchant-')
            ? selectedMerchantAddress.id 
            : undefined,
          toAddress: selectedMerchantAddress?.id.toString().startsWith('merchant-')
            ? selectedMerchantAddress 
            : undefined,
          ...(parcels && { parcels }), // Only include parcels if available
          rateId: selectedRate.id,
          amount: paymentAmount,
          currency: selectedRate.currency,
          paymentMethod: paymentMethod.payment_method,
          isReturn
        }).unwrap();
        
        if (response.requiresAction) {
          // Handle 3D Secure if needed
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            response.clientSecret
          );
          
          if (error) {
            throw new Error(error.message);
          }
          
          onPaymentSuccess({ paymentIntentId: paymentIntent.id });
        } else {
          onPaymentSuccess({ paymentIntentId: response.paymentIntentId });
        }
      } else {
        // No payment needed, just generate the label - only include parcels if available
        const response = await createReturnShippingLabel({
          fulfillmentGroupId,
          // Group-level returns don't need orderItemId
          fromAddressId: selectedCustomerAddress?.id,
          toAddressId: selectedMerchantAddress?.id && !selectedMerchantAddress.id.toString().startsWith('merchant-')
            ? selectedMerchantAddress.id 
            : undefined,
          toAddress: selectedMerchantAddress?.id.toString().startsWith('merchant-')
            ? selectedMerchantAddress 
            : undefined,
          ...(parcels && { parcels }), // Only include parcels if available
          rateId: selectedRate.id,
          isReturn
        }).unwrap();
        
        // Set label data and show success UI
        if (response.success) {
          setLabelData(response);
          setShowLabelSuccess(true);
          toast.success('Return shipping label generated successfully!');
        }
      }
    } catch (err) {
      // Check for customs-related errors and show more specific messages
      if (err.message && (
        err.message.includes('customs declaration') || 
        err.message.includes('international shipment')
      )) {
        const customsError = 'This appears to be an international shipment which requires customs information. Please ensure both addresses have correct country codes.';
        toast.error(customsError);
        onPaymentError(customsError);
      } else {
        toast.error(err.message || 'Failed to generate label');
        onPaymentError(err.message || 'Failed to generate label');
      }
    }
  };

  return {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    isGeneratingReturnLabelApi,
    labelData,
    showLabelSuccess,
    handleGenerateLabel
  };
}; 